import { getClientDeviceId } from './clientDevice.js'

const LINE_CACHE = 'boxim.api.lines'
const LAST_LINE = 'boxim.api.lastLine'
const listeners = []
const failures = new Map()
const circuitUntil = new Map()
let policyTimer = null
let currentPolicy = null

const defaultLine = {
	lineCode: 'default', lineName: '默认线路', apiBaseUrl: process.env.VUE_APP_BASE_API,
	wsUrl: process.env.VUE_APP_WS_URL, uploadBaseUrl: process.env.VUE_APP_BASE_API, weight: 100,
	priority: 1, fallbackLine: true, probeTimeoutMs: 1500, failureThreshold: 3, circuitSeconds: 60
}

const parseBuiltins = () => {
	const raw = process.env.VUE_APP_API_LINES || ''
	return raw.split(',').map((item, index) => {
		const [apiBaseUrl, wsUrl, weight] = item.split('|')
		return apiBaseUrl && wsUrl ? { ...defaultLine, lineCode: `builtin-${index + 1}`, lineName: `内置线路${index + 1}`,
			apiBaseUrl, wsUrl, uploadBaseUrl: apiBaseUrl, weight: Number(weight) || 100, fallbackLine: false, priority: index + 10 } : null
	}).filter(Boolean)
}

const readCache = () => {
	try {
		return JSON.parse(localStorage.getItem(LINE_CACHE) || '[]').filter(line => !(line?.lineCode === 'default' &&
			/^https:\/\/(?:web\.)?ru1qa3rx\.top\/api\/?$/i.test(String(line.apiBaseUrl || ''))))
	} catch (_) { return [] }
}
const normalize = line => ({ ...defaultLine, ...line, apiBaseUrl: String(line.apiBaseUrl || '').replace(/\/$/, ''),
	uploadBaseUrl: String(line.uploadBaseUrl || line.apiBaseUrl || '').replace(/\/$/, '') })
const isValidLine = line => line && (/^https?:\/\//i.test(line.apiBaseUrl) || line.apiBaseUrl.startsWith('/')) && /^wss?:\/\//i.test(line.wsUrl)
const mergeLines = (...groups) => {
	const map = new Map()
	groups.flat().filter(isValidLine).forEach(line => map.set(line.lineCode || line.apiBaseUrl, normalize(line)))
	return [...map.values()].filter(line => line.enabled !== false)
}

const builtinLines = mergeLines([defaultLine], parseBuiltins())
let lines = mergeLines(builtinLines, readCache())
const remembered = localStorage.getItem(LAST_LINE)
let current = lines.find(line => line.lineCode === remembered) || lines.find(line => line.fallbackLine) || lines[0]

const hash = value => {
	let result = 2166136261
	for (let i = 0; i < value.length; i++) result = Math.imul(result ^ value.charCodeAt(i), 16777619)
	return result >>> 0
}
const healthyCandidates = excludeCode => lines.filter(line => line.lineCode !== excludeCode && (circuitUntil.get(line.lineCode) || 0) <= Date.now())
const ranked = candidates => candidates.map(line => {
	const uniform = (hash(`${getClientDeviceId()}:${line.lineCode}`) + 1) / 4294967297
	return { line, score: -Math.log(uniform) / Math.max(1, line.weight || 100) }
})
	.sort((a, b) => a.score - b.score || (a.line.priority || 100) - (b.line.priority || 100)).map(item => item.line)

const fetchWithTimeout = async (url, timeout, options = {}) => {
	const controller = new AbortController()
	const timer = setTimeout(() => controller.abort(), timeout)
	try { return await fetch(url, { method: 'GET', cache: 'no-store', ...options, signal: controller.signal }) } finally { clearTimeout(timer) }
}
const probe = async line => {
	try {
		const response = await fetchWithTimeout(`${line.apiBaseUrl}/open/api-lines/health?_=${Date.now()}`, line.probeTimeoutMs)
		return response.ok
	} catch (_) { return false }
}
const activate = line => {
	if (!line || current.lineCode === line.lineCode) return false
	current = line; failures.set(line.lineCode, 0); localStorage.setItem(LAST_LINE, line.lineCode)
	listeners.forEach(listener => listener(line))
	reportCurrentLine()
	return true
}
const loadRemote = async line => {
	try {
		const response = await fetchWithTimeout(`${line.apiBaseUrl}/open/api-lines`, Math.max(2000, line.probeTimeoutMs))
		const result = await response.json()
		if (response.ok && result.code === 200 && Array.isArray(result.data)) {
			lines = mergeLines(builtinLines, result.data); localStorage.setItem(LINE_CACHE, JSON.stringify(result.data)); return true
		}
	} catch (_) { /* 保留内置及缓存线路 */ }
	return false
}
const start = async () => {
	await new Promise(resolve => setTimeout(resolve, Math.floor(Math.random() * 1200)))
	await loadRemote(current)
	const currentConfigured = lines.some(line => line.lineCode === current.lineCode)
	const preferred = remembered && currentConfigured ? current : ranked(lines)[0]
	const candidate = preferred.lineCode === current.lineCode ? ranked(healthyCandidates(current.lineCode))[0] : preferred
	const checks = await Promise.all([probe(current), candidate ? probe(candidate) : Promise.resolve(false)])
	if ((!remembered || !currentConfigured) && checks[1] && candidate.lineCode !== current.lineCode) activate(candidate)
	else if (!checks[0] && checks[1]) activate(candidate)
	else if (!checks[0]) await switchLine()
}
const switchLine = async () => {
	const available = ranked(healthyCandidates(current.lineCode))
	const fallback = available.find(line => line.fallbackLine)
	const candidates = available.slice(0, 2)
	if (fallback && !candidates.some(line => line.lineCode === fallback.lineCode)) candidates[candidates.length - 1] = fallback
	const checks = await Promise.all(candidates.map(async line => ({ line, ok: await probe(line) })))
	const next = checks.find(item => item.ok)?.line
	if (next) activate(next)
	return next
}
const markSuccess = () => failures.set(current.lineCode, 0)
const markFailure = async () => {
	const count = (failures.get(current.lineCode) || 0) + 1; failures.set(current.lineCode, count)
	if (count < current.failureThreshold) return false
	circuitUntil.set(current.lineCode, Date.now() + current.circuitSeconds * 1000)
	return !!(await switchLine())
}

const reportCurrentLine = async () => {
	const token = sessionStorage.getItem('accessToken')
	if (!token) return
	try {
		await fetchWithTimeout(`${current.apiBaseUrl}/user/api-line/report`, 3000, {
			method: 'POST', headers: { 'Content-Type': 'application/json', accessToken: encodeURIComponent(token),
				'X-Device-Id': getClientDeviceId(), 'X-Client-Terminal': '0' }, body: JSON.stringify({ lineCode: current.lineCode })
		})
	} catch (_) { /* 线路上报不影响正常业务 */ }
}

const applyPolicy = async policy => {
	if (policyTimer) clearTimeout(policyTimer)
	policyTimer = null
	currentPolicy = policy && policy.mode === 'TEMP_PREFERRED' ? policy : null
	if (!currentPolicy) { reportCurrentLine(); return false }
	const expiresAt = Number(currentPolicy.expiresTime || 0)
	if (!expiresAt || expiresAt <= Date.now()) { currentPolicy = null; reportCurrentLine(); return false }
	if (currentPolicy.line) lines = mergeLines(lines, [currentPolicy.line])
	const preferred = lines.find(line => line.lineCode === currentPolicy.lineCode)
	policyTimer = setTimeout(() => syncPolicy(), Math.min(expiresAt - Date.now() + 1000, 2147483647))
	if (!preferred || preferred.lineCode === current.lineCode) { reportCurrentLine(); return false }
	if (await probe(preferred)) return activate(preferred)
	circuitUntil.set(preferred.lineCode, Date.now() + preferred.circuitSeconds * 1000)
	reportCurrentLine()
	return false
}

const syncPolicy = async () => {
	const token = sessionStorage.getItem('accessToken')
	if (!token) return false
	try {
		const response = await fetchWithTimeout(`${current.apiBaseUrl}/user/api-line/effective?_=${Date.now()}`, 4000, {
			headers: { accessToken: encodeURIComponent(token), 'X-Device-Id': getClientDeviceId(), 'X-Client-Terminal': '0' }
		})
		const result = await response.json()
		if (response.ok && result.code === 200) return await applyPolicy(result.data)
	} catch (_) { /* 当前线路不可用时由常规熔断机制处理 */ }
	return false
}

start()

export default {
	getApiBase: () => current.apiBaseUrl,
	getUploadBase: () => current.uploadBaseUrl || current.apiBaseUrl,
	getWsUrl: () => current.wsUrl,
	getCurrentLine: () => current,
	markSuccess,
	markFailure,
	switchLine,
	syncPolicy,
	reportCurrentLine,
	onChange: listener => listeners.push(listener)
}
